import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAlerts } from "../AlertsContext";
import { ALERTS_ENABLED } from "../constants";
import { ALERT_CATEGORIES } from "../services/alertsMockService";

dayjs.extend(relativeTime);

/** Configuración de presentación por categoría (label + icono + orden). */
const CATEGORY_CONFIG = [
  {
    key: ALERT_CATEGORIES.CLINICAL_NOTES,
    label: "Pending clinical notes",
    Icon: DescriptionOutlinedIcon,
  },
  {
    key: ALERT_CATEGORIES.CARE_GAPS,
    label: "Care gaps reviewed",
    Icon: FactCheckOutlinedIcon,
  },
  {
    key: ALERT_CATEGORIES.PHYSICIAN_QUERIES,
    label: "Billing queries (physician)",
    Icon: AssignmentOutlinedIcon,
  },
  {
    key: ALERT_CATEGORIES.PATIENT_OBSERVATIONS,
    label: "Patient observations",
    Icon: VisibilityOutlinedIcon,
  },
  {
    key: ALERT_CATEGORIES.ASSIGNED_TASKS,
    label: "Assigned tasks",
    Icon: AssignmentOutlinedIcon,
  },
];

/** Item individual de alerta dentro de una categoría. */
const AlertItem = ({ alert, onRead }) => (
  <ListItemButton
    onClick={() => onRead(alert.alertId)}
    alignItems="flex-start"
    sx={{
      borderRadius: 1,
      mb: 0.5,
      backgroundColor: alert.read ? "transparent" : "action.hover",
      "&:hover": { backgroundColor: "action.selected" },
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        mt: 1,
        mr: 1.5,
        flexShrink: 0,
        backgroundColor: alert.read ? "transparent" : "error.main",
        border: alert.read ? "1px solid" : "none",
        borderColor: "divider",
      }}
    />
    <ListItemText
      primary={
        <Typography
          variant="body2"
          sx={{ fontWeight: alert.read ? 400 : 600, lineHeight: 1.3 }}
        >
          {alert.title}
        </Typography>
      }
      secondary={
        <>
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.25 }}
          >
            {alert.description}
          </Typography>
          <Typography
            component="span"
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mt: 0.25 }}
          >
            {dayjs(alert.createdAt).fromNow()}
          </Typography>
        </>
      }
    />
  </ListItemButton>
);

/**
 * Panel lateral DERECHO de notificaciones (MUI Drawer temporal).
 * Muestra las alertas agrupadas por categoría. Clic en una alerta la
 * marca como leída; "Mark all as read" limpia todo el contador.
 */
const AlertsDrawer = () => {
  const alertsCtx = useAlerts();
  // DEV-ONLY mientras las alertas sean mock (ver constants.js).
  if (!ALERTS_ENABLED || !alertsCtx) return null;

  const {
    alerts,
    loading,
    unreadCount,
    drawerOpen,
    closeDrawer,
    markAsRead,
    markAllAsRead,
  } = alertsCtx;

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeDrawer}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 380 }, maxWidth: "100%" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} new`} size="small" color="error" />
          )}
        </Stack>
        <IconButton
          onClick={closeDrawer}
          size="small"
          aria-label="close notifications"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, pb: 1 }}>
        <Button
          size="small"
          startIcon={<DoneAllIcon />}
          onClick={markAllAsRead}
          disabled={unreadCount === 0}
          sx={{ textTransform: "none" }}
        >
          Mark all as read
        </Button>
      </Box>
      <Divider />

      {/* Cuerpo: grupos por categoría */}
      <Box sx={{ overflowY: "auto", flexGrow: 1, px: 2, py: 1.5 }}>
        {loading && alerts.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : alerts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <NotificationsNoneIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">You're all caught up</Typography>
          </Box>
        ) : (
          CATEGORY_CONFIG.map(({ key, label, Icon }) => {
            const group = alerts.filter((a) => a.category === key);
            if (group.length === 0) return null;
            const groupUnread = group.filter((a) => !a.read).length;

            return (
              <Box key={key} sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.5 }}
                >
                  <Icon fontSize="small" color="action" />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    {label}
                  </Typography>
                  {groupUnread > 0 && (
                    <Chip
                      label={groupUnread}
                      size="small"
                      color="error"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  )}
                </Stack>
                <List dense disablePadding>
                  {group.map((alert) => (
                    <AlertItem
                      key={alert.alertId}
                      alert={alert}
                      onRead={markAsRead}
                    />
                  ))}
                </List>
              </Box>
            );
          })
        )}
      </Box>
    </Drawer>
  );
};

export default AlertsDrawer;
